import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  InputPassword,
  TextBlock,
  toast,
} from '@skillarea/lib-frontend';
import { createFileRoute } from '@tanstack/react-router';

import styles from './maintenance.module.scss';
import {
  INTERNAL_MAINTENANCE_DEFAULT_MESSAGE,
  clearInternalMaintenanceCredentials,
  encodeBasicAuthCredentials,
  getInternalMaintenanceErrorMessage,
  normalizeInternalMaintenanceStatus,
  readInternalMaintenanceCredentials,
  saveInternalMaintenanceCredentials,
  shouldBlinkMaintenanceTitle,
  startMaintenanceTitleBlink,
  type TInternalMaintenanceCredentials,
  type TInternalMaintenanceStatus,
} from './-maintenance.lib';

import { LayoutCard } from '~/components/LayoutCard/LayoutCard';

export const Route = createFileRoute('/internal/maintenance')({
  component: InternalMaintenancePage,
});

const POLL_INTERVAL_MS = 2000;

type TActionState = 'authenticate' | 'cancel' | 'enable' | null;

type TRequestOptions = {
  credentials: TInternalMaintenanceCredentials;
  method?: 'GET' | 'POST';
  path?: string;
  body?: unknown;
};

type TRequestError = Error & {
  status?: number;
};

function formatTimestamp(value?: number): string {
  if (typeof value !== 'number') {
    return '-';
  }

  return new Date(value).toLocaleString();
}

async function requestMaintenanceStatus({
  body,
  credentials,
  method = 'GET',
  path = '/api/internal/billing/maintenance',
}: TRequestOptions): Promise<TInternalMaintenanceStatus> {
  const response = await fetch(path, {
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
    headers: {
      Authorization: encodeBasicAuthCredentials(credentials),
      'Content-Type': 'application/json',
    },
    method,
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(
      getInternalMaintenanceErrorMessage(payload, 'Request failed'),
    ) as TRequestError;
    error.status = response.status;
    throw error;
  }

  return normalizeInternalMaintenanceStatus(payload);
}

function InternalMaintenancePage() {
  const [credentials, setCredentials] =
    useState<TInternalMaintenanceCredentials | null>(null);
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState(INTERNAL_MAINTENANCE_DEFAULT_MESSAGE);
  const [status, setStatus] = useState<TInternalMaintenanceStatus | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<TActionState>(null);
  const syncMessageRef = useRef(true);

  const isAuthenticated = credentials !== null;
  const isBusy = actionState !== null;
  const statusLabel = useMemo(() => {
    if (!status) {
      return 'Unknown';
    }

    return status.enabled ? 'Active' : 'Inactive';
  }, [status]);

  const syncStatus = useCallback(
    async (
      nextCredentials: TInternalMaintenanceCredentials,
    ): Promise<TInternalMaintenanceStatus> => {
      const nextStatus = await requestMaintenanceStatus({
        credentials: nextCredentials,
      });
      setStatus(nextStatus);
      setRequestError(null);

      if (syncMessageRef.current) {
        setMessage(nextStatus.message ?? INTERNAL_MAINTENANCE_DEFAULT_MESSAGE);
      }

      return nextStatus;
    },
    [],
  );

  const clearSession = useCallback((messageText?: string): void => {
    if (typeof window === 'undefined') {
      return;
    }

    clearInternalMaintenanceCredentials(window.sessionStorage);
    setCredentials(null);
    setStatus(null);
    setPassword('');
    if (messageText) {
      setAuthError(messageText);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let isActive = true;
    const storedCredentials = readInternalMaintenanceCredentials(
      window.sessionStorage,
    );

    if (!storedCredentials) {
      return;
    }

    setPassword(storedCredentials.password);
    setCredentials(storedCredentials);
    setActionState('authenticate');

    void syncStatus(storedCredentials)
      .then(() => {
        if (!isActive) {
          return;
        }

        setAuthError(null);
      })
      .catch((error: unknown) => {
        if (!isActive) {
          return;
        }

        const nextError =
          error instanceof Error
            ? error
            : new Error('Unable to restore maintenance session');

        clearSession(nextError.message);
      })
      .finally(() => {
        if (!isActive) {
          return;
        }

        setActionState(null);
      });

    return () => {
      isActive = false;
    };
  }, [clearSession, syncStatus]);

  useEffect(() => {
    if (!credentials) {
      return;
    }

    let isActive = true;

    const pollStatus = async (): Promise<void> => {
      try {
        await syncStatus(credentials);
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }

        const nextError =
          error instanceof Error
            ? error
            : new Error('Unable to refresh maintenance status');

        const requestError = nextError as TRequestError;
        if (requestError.status === 401) {
          clearSession('Stored password is no longer valid');
          return;
        }

        setRequestError(nextError.message);
      }
    };

    const intervalId = window.setInterval(() => {
      void pollStatus();
    }, POLL_INTERVAL_MS);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [clearSession, credentials, syncStatus]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!shouldBlinkMaintenanceTitle(status)) {
      return;
    }

    return startMaintenanceTitleBlink(document);
  }, [status]);

  const handleAuthenticate = async (): Promise<void> => {
    const nextCredentials = {
      password: password.trim(),
    };

    if (!nextCredentials.password) {
      setAuthError('Enter internal maintenance password');
      return;
    }

    setActionState('authenticate');
    try {
      const nextStatus = await syncStatus(nextCredentials);

      if (typeof window !== 'undefined') {
        saveInternalMaintenanceCredentials(
          window.sessionStorage,
          nextCredentials,
        );
      }

      setCredentials(nextCredentials);
      setAuthError(null);
      setMessage(nextStatus.message ?? INTERNAL_MAINTENANCE_DEFAULT_MESSAGE);
      syncMessageRef.current = true;
    } catch (error: unknown) {
      const nextError =
        error instanceof Error ? error : new Error('Unable to authenticate');
      setAuthError(nextError.message);
      setCredentials(null);
    } finally {
      setActionState(null);
    }
  };

  const handleEnable = async (): Promise<void> => {
    if (!credentials) {
      return;
    }

    const nextMessage = message.trim();
    if (!nextMessage) {
      setRequestError('Maintenance message is required');
      return;
    }

    setActionState('enable');
    try {
      const nextStatus = await requestMaintenanceStatus({
        body: { message: nextMessage },
        credentials,
        method: 'POST',
        path: '/api/internal/billing/maintenance/enable',
      });
      setStatus(nextStatus);
      setRequestError(null);
      setMessage(nextStatus.message ?? nextMessage);
      syncMessageRef.current = true;
      toast?.success('Maintenance enabled');
    } catch (error: unknown) {
      const nextError =
        error instanceof Error
          ? error
          : new Error('Unable to enable maintenance');
      setRequestError(nextError.message);
    } finally {
      setActionState(null);
    }
  };

  const handleCancel = async (): Promise<void> => {
    if (!credentials) {
      return;
    }

    setActionState('cancel');
    try {
      const nextStatus = await requestMaintenanceStatus({
        credentials,
        method: 'POST',
        path: '/api/internal/billing/maintenance/cancel',
      });
      setStatus(nextStatus);
      setRequestError(null);
      setMessage(INTERNAL_MAINTENANCE_DEFAULT_MESSAGE);
      syncMessageRef.current = true;
      toast?.success('Maintenance cancelled');
    } catch (error: unknown) {
      const nextError =
        error instanceof Error
          ? error
          : new Error('Unable to cancel maintenance');
      setRequestError(nextError.message);
    } finally {
      setActionState(null);
    }
  };

  const handleLogout = (): void => {
    clearSession();
    setRequestError(null);
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <LayoutCard
          className={styles.card}
          title="Internal maintenance operator"
        >
          <div className={styles.stack}>
            <TextBlock color="secondary">
              Authenticate with the internal Basic auth password. Password is
              stored only in this browser session.
            </TextBlock>

            <div className={styles.formRow}>
              <div className={styles.field}>
                <TextBlock color="contrast" weight="600">
                  Password
                </TextBlock>
                <InputPassword
                  data-testid="internal-operator-password"
                  onChange={setPassword}
                  placeholder="Internal password"
                  value={password}
                />
              </div>
            </div>

            {authError ? (
              <TextBlock className={styles.dangerText} color="error">
                {authError}
              </TextBlock>
            ) : null}

            <div className={styles.actions}>
              <Button
                color="accent"
                data-testid="internal-auth-submit"
                disabled={isBusy}
                onClick={() => {
                  void handleAuthenticate();
                }}
                size="s"
                type="button"
                variant="outline"
              >
                {actionState === 'authenticate'
                  ? 'Checking...'
                  : 'Authenticate'}
              </Button>
              {isAuthenticated ? (
                <Button
                  color="accent"
                  data-testid="maintenance-auth-clear"
                  disabled={isBusy}
                  onClick={handleLogout}
                  size="s"
                  type="button"
                  variant="outline"
                >
                  Forget session
                </Button>
              ) : null}
            </div>
          </div>
        </LayoutCard>

        <LayoutCard className={styles.card} title="Maintenance status">
          <div className={styles.stack}>
            <div className={styles.statusGrid}>
              <div className={styles.statusItem}>
                <TextBlock color="secondary">Status</TextBlock>
                <TextBlock
                  className={styles.statusValue}
                  color={status?.enabled ? 'warning' : 'contrast'}
                  data-testid="maintenance-status-indicator"
                  weight="600"
                >
                  {statusLabel}
                </TextBlock>
              </div>

              <div className={styles.statusItem}>
                <TextBlock color="secondary">Platform active games</TextBlock>
                <TextBlock
                  className={styles.statusValue}
                  color="contrast"
                  data-testid="maintenance-active-games-count"
                  weight="600"
                >
                  {status?.activeGamesCount ?? '-'}
                </TextBlock>
              </div>

              <div className={styles.statusItem}>
                <TextBlock color="secondary">Started by</TextBlock>
                <TextBlock
                  className={styles.statusValue}
                  color="contrast"
                  weight="600"
                >
                  {status?.startedBy ?? '-'}
                </TextBlock>
              </div>

              <div className={styles.statusItem}>
                <TextBlock color="secondary">Started at</TextBlock>
                <TextBlock
                  className={styles.statusValue}
                  color="contrast"
                  weight="600"
                >
                  {formatTimestamp(status?.startedAtMs)}
                </TextBlock>
              </div>
            </div>

            <div className={styles.field}>
              <TextBlock color="secondary">Current message</TextBlock>
              <TextBlock
                className={styles.statusValue}
                color="contrast"
                weight="600"
              >
                {status?.message ?? '-'}
              </TextBlock>
            </div>

            {requestError ? (
              <TextBlock className={styles.dangerText} color="error">
                {requestError}
              </TextBlock>
            ) : null}
          </div>
        </LayoutCard>

        <LayoutCard className={styles.card} title="Operator controls">
          <div className={styles.stack}>
            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                Maintenance message
              </TextBlock>
              <textarea
                className={styles.messageInput}
                data-testid="maintenance-message"
                disabled={!isAuthenticated || isBusy}
                onChange={(event) => {
                  syncMessageRef.current = false;
                  setMessage(event.target.value);
                }}
                value={message}
              />
            </div>

            <div className={styles.actions}>
              <Button
                color="accent"
                data-testid="maintenance-enable"
                disabled={!isAuthenticated || isBusy}
                onClick={() => {
                  void handleEnable();
                }}
                size="s"
                type="button"
                variant="outline"
              >
                {actionState === 'enable'
                  ? 'Enabling...'
                  : 'Enable maintenance'}
              </Button>
              <Button
                color="accent"
                data-testid="maintenance-cancel"
                disabled={!isAuthenticated || isBusy}
                onClick={() => {
                  void handleCancel();
                }}
                size="s"
                type="button"
                variant="outline"
              >
                {actionState === 'cancel'
                  ? 'Cancelling...'
                  : 'Cancel maintenance'}
              </Button>
            </div>
          </div>
        </LayoutCard>
      </div>
    </div>
  );
}
